<template>
  <section id="hero" class="hero" @touchstart="handleTouchStart" @touchend="handleTouchEnd" @mousedown="handleMouseDown"
    @mouseup="handleMouseUp">
    <transition name="fade">
      <div :key="backgroundImage" class="hero-bg-image" :style="{ backgroundImage: `url(${backgroundImage})` }"></div>
    </transition>

    <div class="hero-content container px-3" data-aos="fade-in" data-aos-delay="300">
      <div :key="currentSlideIndex" class="text-center">

        <h1 class="hero-title fw-bolder mb-3">
          <span class="d-none d-md-block display-3">{{ currentSlide.title }}</span>
          <span class="d-block d-md-none display-5">{{ currentSlide.mobileTitle }}</span>
        </h1>

        <p class="hero-description fw-bolder mb-4 mx-auto" style="max-width: 700px;">
          <span class="d-none d-md-block lead">{{ currentSlide.description }}</span>
          <span class="d-block d-md-none fs-6">{{ currentSlide.mobileDescription }}</span>
        </p>

        <RouterLink :to="currentSlide.link"
          class="btn btn-tactical btn-lg px-4 py-3 shadow-lg d-inline-flex align-items-center justify-content-center">
          <i :class="['bi', currentSlide.icon, 'me-2']"></i>
          <span class="d-none d-md-block">{{ currentSlide.buttonText }}</span>
          <span class="d-block d-md-none fw-bold">{{ currentSlide.mobileButtonText }}</span>
        </RouterLink>

      </div>
    </div>

    <div class="carousel-indicators-custom">
      <span v-for="(slide, index) in slides" :key="index" :class="{ active: index === currentSlideIndex }"
        @click="goToSlide(index)"></span>
    </div>

    <Particles id="tsparticles" :init="particlesInit" :options="particleConfig" />
    <div class="smoke-layer"></div>
  </section>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from "vue";
import { loadSlim } from "tsparticles-slim";

// Importación de las 11 imágenes
import tactical01 from "@/assets/img/Tactical/tactical_01.jpg";
import tactical02 from "@/assets/img/Tactical/tactical_02.jpg";
import tactical03 from "@/assets/img/Tactical/tactical_03.jpg";
import tactical04 from "@/assets/img/Tactical/tactical_04.jpg";
import tactical05 from "@/assets/img/Tactical/tactical_05.jpg";
import tactical06 from "@/assets/img/Tactical/tactical_06.jpg";
import tactical07 from "@/assets/img/Tactical/tactical_07.jpg";
import tactical08 from "@/assets/img/Tactical/tactical_08.jpg";
import tactical09 from "@/assets/img/Tactical/tactical_09.jpg";
import tactical10 from "@/assets/img/Tactical/tactical_10.jpg";
import tactical11 from "@/assets/img/Tactical/tactical_11.jpg";

const allHeroImages = [
  tactical01, tactical02, tactical03, tactical04, tactical05,
  tactical06, tactical07, tactical08, tactical09, tactical10, tactical11
];

const backgroundImage = ref(allHeroImages[0]);
const currentSlideIndex = ref(0);
let intervalId = null;

const touchStartX = ref(0);
const touchEndX = ref(0);

// --- ESTRUCTURA DE SLIDES MEJORADA (Textos Cortos e Iconos) ---
const slides = [
  {
    // Desktop
    title: "ENTRENA PRESENCIALMENTE",
    description: "Entrenamiento táctico práctico y real, impartido por instructores de élite con experiencia militar y policial.",
    buttonText: "ENCUENTRA UN CURSO CERCA DE TI",
    // Mobile
    mobileTitle: "ENTRENAMIENTO PRESENCIAL",
    mobileDescription: "Entrenamiento práctico y real con instructores de élite.",
    mobileButtonText: "BUSCAR CURSO",
    link: "/live-training",
    icon: "bi-geo-alt-fill" // Ícono de GPS
  },
  {
    // Desktop
    title: "ENTRENA EN CUALQUIER LUGAR, EN CUALQUIER MOMENTO",
    description: "Nuestros cursos virtuales llevan entrenamiento en defensa personal y seguridad con armas directamente a tu dispositivo.",
    buttonText: "COMIENZA EL ENTRENAMIENTO EN LÍNEA",
    // Mobile
    mobileTitle: "ENTRENAMIENTO VIRTUAL",
    mobileDescription: "Defensa personal y seguridad directamente en tu dispositivo.",
    mobileButtonText: "INICIAR AHORA",
    link: "/virtual-training",
    icon: "bi-laptop" // Ícono de Computadora/Virtual
  },
  {
    // Desktop
    title: "EQUIPO DISEÑADO POR GUERREROS",
    description: "Equípate con accesorios tácticos y herramientas EDC probados en campo y aprobados por operadores profesionales en todo el mundo.",
    buttonText: "COMPRAR EQUIPO TÁCTICO",
    // Mobile
    mobileTitle: "EQUIPO TÁCTICO",
    mobileDescription: "Herramientas EDC y accesorios probados en el campo de acción.",
    mobileButtonText: "VER TIENDA",
    link: "/shop",
    icon: "bi-shield-shaded" // Ícono de Escudo/Equipo
  }
];

const currentSlide = computed(() => slides[currentSlideIndex.value]);

// Lógica de navegación y cambio de imagen
const startTimer = () => {
  stopTimer();
  intervalId = setInterval(nextSlide, 6000);
};

const stopTimer = () => { if (intervalId) clearInterval(intervalId); };

const goToSlide = (index) => {
  currentSlideIndex.value = index;
  changeBackground();
  startTimer();
};

onMounted(() => {
  allHeroImages.forEach(src => {
    const img = new Image();
    img.src = src;
  });
  startTimer();
});
onUnmounted(() => stopTimer());

// Configuración de Partículas
const particlesInit = async (engine) => { await loadSlim(engine); };
const particleConfig = {
  background: { color: "transparent" },
  particles: {
    number: { value: 40 },
    color: { value: "#00aaff" },
    opacity: { value: 0.15 },
    size: { value: 3 },
    move: { enable: true, speed: 1.2, outModes: "out" },
  },
};

const changeBackground = () => {
  backgroundImage.value = allHeroImages[Math.floor(Math.random() * allHeroImages.length)];
};

const nextSlide = () => {
  currentSlideIndex.value = (currentSlideIndex.value + 1) % slides.length;
  changeBackground();
};

const prevSlide = () => {
  currentSlideIndex.value = (currentSlideIndex.value - 1 + slides.length) % slides.length;
  changeBackground();
};

// Eventos de Touch (Móvil)
const handleTouchStart = (e) => { touchStartX.value = e.screenX || e.touches[0].clientX; };
const handleTouchEnd = (e) => {
  touchEndX.value = e.screenX || e.changedTouches[0].clientX;
  handleSwipe();
};

// Eventos de Mouse (Escritorio)
const handleMouseDown = (e) => { touchStartX.value = e.clientX; };
const handleMouseUp = (e) => {
  touchEndX.value = e.clientX;
  handleSwipe();
};

// Calcular si se deslizó a la izquierda o derecha
const handleSwipe = () => {
  const swipeThreshold = 50; // Mínimo de píxeles arrastrados para contar como "swipe"
  if (touchStartX.value - touchEndX.value > swipeThreshold) {
    nextSlide(); // Arrastre a la izquierda
    startTimer(); // Reiniciamos el timer para que no se cambie solo inmediatamente
  } else if (touchEndX.value - touchStartX.value > swipeThreshold) {
    prevSlide(); // Arrastre a la derecha
    startTimer();
  }
};
</script>

<style scoped>
/* --- Título Principal --- */
.hero-title,
.hero-title .display-3,
.hero-title .display-5 {
  color: #ffffff !important;
  font-weight: 900 !important;
  /* 900 es el máximo grosor (Black) */
  text-shadow: 0px 4px 15px rgba(0, 0, 0, 0.9);
  /* Sombra más oscura para resaltar */
  letter-spacing: 2px;
  text-transform: uppercase;
  /* Opcional: Si tienes una fuente específica militar, ponla aquí */
  /* font-family: 'Montserrat', sans-serif !important; */
}

/* --- Descripción --- */
.hero-description,
.hero-description .lead,
.hero-description .fs-6 {
  color: #f8f9fa !important;
  /* Un blanco casi puro pero suave */
  font-weight: 700 !important;
  /* 700 es Bold, ideal para descripciones gruesas pero legibles */
  text-shadow: 0px 2px 10px rgba(0, 0, 0, 0.95);
  /* Sombra fuerte para contraste */
  letter-spacing: 0.5px;
}

.hero {
  position: relative;
  height: 100vh;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: grab;
}
.hero:active {
  cursor: grabbing;
}

.hero-bg-image {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  filter: brightness(0.7) contrast(1.25);
  z-index: 1;
}

.hero-title {
  color: #ffffff !important;
  text-shadow: 0px 4px 10px rgba(0, 0, 0, 0.7);
  letter-spacing: 1.5px;
  text-transform: uppercase;
}

/* Ajuste específico para pantallas móviles pequeñas (celulares) */
@media (max-width: 575px) {
  .hero-title .display-5 {
    font-size: 2.2rem;
    /* Evita que el título se desborde */
  }

  .hero-description {
    font-size: 1rem;
    /* Letra más legible en móvil */
    padding: 0 15px;
    /* Margen a los lados para que no pegue con la pantalla */
  }
}

.hero-description {
  color: #e0e0e0 !important;
  text-shadow: 0px 2px 8px rgba(0, 0, 0, 0.9);
  font-weight: 600;
}

.hero-content {
  position: relative;
  z-index: 20;
}

.btn-tactical {
  background-color: #ff3131 !important;
  color: #ffffff !important;
  border: none !important;
  font-weight: 800 !important;
  letter-spacing: 1px;
  text-transform: uppercase;
  transition: all 0.3s ease-in-out !important;
  border-radius: 5px;
}

.btn-tactical:hover {
  background-color: #d62828 !important;
  transform: scale(1.05);
  box-shadow: 0 10px 20px rgba(255, 49, 49, 0.3) !important;
}

.btn-tactical:focus,
.btn-tactical:active {
  background-color: #b31d1d !important;
  box-shadow: none !important;
  outline: none !important;
}

.carousel-indicators-custom {
  position: absolute;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 15;
  display: flex;
  gap: 15px;
}

.carousel-indicators-custom span {
  width: 12px;
  height: 12px;
  border: 2px solid #fff;
  border-radius: 50%;
  cursor: pointer;
  transition: 0.3s;
}

.carousel-indicators-custom span.active {
  background: #ff3131;
  /* Un toque rojo en los indicadores para mantener estética */
  border-color: #ff3131;
  transform: scale(1.3);
}

#tsparticles {
  position: absolute;
  inset: 0;
  z-index: 3;
  pointer-events: none;
}

.smoke-layer {
  position: absolute;
  inset: 0;
  background: url("https://c4.wallpaperflare.com/wallpaper/448/699/737/abstract-digital-art-3d-abstract-lines-wallpaper-preview.jpg");
  opacity: 0.2;
  mix-blend-mode: screen;
  animation: smokeMove 10s infinite linear;
  z-index: 4;
  pointer-events: none;
}

@keyframes smokeMove {
  0% {
    transform: translateY(0) scale(1.1);
  }

  100% {
    transform: translateY(-8%) scale(1.2);
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 1.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>